import * as React from "react";
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApiClient } from "../../../api";

import { BarChart } from "@mui/x-charts";
import { QuestionAccordion } from "../../question/components/QuestionAccordion";

export const OverallPerformanceChart = ({ analysisData }) => {
  const theme = useTheme();
  const dataset = useMemo(
    () =>
      analysisData.map((q, idx) => ({
        question: `Q${idx + 1}`,
        correct: q.percentageCorrect,
        incorrect: q.percentageIncorrect,
      })),
    [analysisData],
  );
  return (
    <BarChart
      sx={{ mt: 8 }}
      dataset={dataset}
      borderRadius={10}
      xAxis={[
        {
          dataKey: "question",
        },
      ]}
      series={[
        {
          dataKey: "correct",
          label: "% Correct",
          color: theme.palette.primary.main,
        },
        {
          dataKey: "incorrect",
          label: "% Incorrect",
          color: theme.palette.error.light,
        },
      ]}
      yAxis={[
        {
          label: "% of students",
          min: 0,
          max: 100, // always 0–100%
          width: 60,
        },
      ]}
      height={250}
      margin={{ left: 0, top: 20, right: 20 }}
      slotProps={{
        legend: {
          direction: "horizontal",
          position: { vertical: "bottom", horizontal: "center" },
        },
      }}
    />
  );
};

export const QuestionOptionChart = ({ selectedQuestion }) => {
  const theme = useTheme();
  const totalResponses = selectedQuestion.options.reduce(
    (sum, opt) => sum + opt.selectedCount,
    0,
  );

  const dataset = selectedQuestion.options.map((opt, idx) => ({
    option: `Option ${idx + 1}`,
    percentage: totalResponses ? (opt.selectedCount / totalResponses) * 100 : 0,
    isCorrect: opt.isCorrect,
  }));

  return (
    <BarChart
      sx={{ mt: 8 }}
      dataset={dataset}
      xAxis={[
        {
          dataKey: "option",
          colorMap: {
            type: "ordinal",
            colors: dataset.map((opt) =>
              opt.isCorrect
                ? theme.palette.primary.main
                : theme.palette.error.light,
            ),
          },
        },
      ]}
      borderRadius={10}
      series={[
        {
          dataKey: "percentage",
          label: "% of responses",
        },
      ]}
      yAxis={[
        {
          min: 0,
          max: 100, // always 0–100%
          label: "% of students",
          width: 60,
        },
      ]}
      height={250}
      margin={{ left: 0, top: 20, right: 20 }}
      hideLegend
    />
  );
};
export const QuizAnalysisGraphs = ({ analysisData, sxProps }) => {
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  const overallHeaderRef = useRef(null);
  const questionHeaderRef = useRef(null);

  useEffect(() => {
    const overallHeight = overallHeaderRef.current?.offsetHeight || 0;
    const questionHeight = questionHeaderRef.current?.offsetHeight || 0;
    setHeaderHeight(Math.max(overallHeight, questionHeight));
  }, [analysisData, selectedQuestionIdx]);

  if (!analysisData || !analysisData.length) return null;

  const selectedQuestion = analysisData[selectedQuestionIdx];
  if (!selectedQuestion) return null;
  return (
    <Card sx={{ width: "100%", ...sxProps }}>
      <CardContent>
        <Grid container spacing={3}>
          {/* Overall Performance Chart */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <div ref={overallHeaderRef} style={{ height: headerHeight }}>
              <Stack direction={"row"} alignItems={"center"}>
                <Typography variant="h6" gutterBottom>
                  Overall Quiz Performance
                </Typography>
              </Stack>
            </div>
            <OverallPerformanceChart analysisData={analysisData} />
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <div ref={questionHeaderRef} style={{ height: headerHeight }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent={"space-between"}
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
              >
                <Typography variant="h6" gutterBottom>
                  Question-Level Responses
                </Typography>
                <FormControl sx={{ width: { xs: "100%", sm: 200 } }}>
                  <InputLabel id="question-select-label">Question</InputLabel>
                  <Select
                    labelId="question-select-label"
                    value={selectedQuestionIdx}
                    label="Question"
                    onChange={(e) =>
                      setSelectedQuestionIdx(Number(e.target.value))
                    }
                  >
                    {analysisData.map((q, idx) => (
                      <MenuItem key={q.question_id} value={idx}>
                        {`Q${idx + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </div>
            <QuestionOptionChart selectedQuestion={selectedQuestion} />
          </Grid>

          <Grid item size={12}>
            <QuestionAccordion
              hideChips
              disableEdit
              question={selectedQuestion}
              number={selectedQuestionIdx + 1}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const QuizQuestionLevelAnalysis = ({ quizGroup, sxProps }) => {
  const [selectedQuiz, setSelectedQuiz] = useState("");
  const [questionLevelAnalysis, setQuestionLevelAnalysis] = useState([]);
  const ApiClient = useApiClient();

  useEffect(() => {
    if (quizGroup?.quizzes?.length === 1) {
      setSelectedQuiz(quizGroup.quizzes[0].id);
    }
  }, [quizGroup]);

  useEffect(() => {
    if (!selectedQuiz) return;
    const subscription = ApiClient.quiz.pollQuizQuestionAnalysis({
      quiz: selectedQuiz,
      pollInterval: 5000,
      onUpdate: (data) => setQuestionLevelAnalysis(data),
    });

    return () => subscription.unsubscribe();
  }, [selectedQuiz]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
        ...sxProps,
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {quizGroup?.quizzes?.length > 1 && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
              <InputLabel id="quiz-label">Quiz</InputLabel>
              <Select
                labelId="quiz-label"
                value={selectedQuiz}
                label="Quiz"
                onChange={(e) => setSelectedQuiz(e.target.value)}
              >
                {quizGroup.quizzes.map((quiz, index) => (
                  <MenuItem key={quiz.id} value={quiz.id}>
                    {quiz.name ? quiz.name : `Quiz ${index + 1}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        <QuizAnalysisGraphs analysisData={questionLevelAnalysis} />
      </CardContent>
    </Card>
  );
};
